import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Bug, Lightbulb, MessageSquare } from "lucide-react";

const CATEGORIES = [
  { key: "bug", label: "Bug", icon: Bug },
  { key: "idea", label: "Idea", icon: Lightbulb },
  { key: "general", label: "General", icon: MessageSquare },
];

export default function FeedbackDialog({ open, onClose }) {
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const reset = () => { setCategory("general"); setMessage(""); };

  const submit = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.post("/feedback", { message: message.trim(), category });
      toast.success("Thanks for the feedback!", { description: "We read every note during the Beta." });
      reset();
      onClose();
    } catch {
      toast.error("Couldn't send feedback. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="feedback-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>Spotted a bug or have an idea? Tell us — you're helping shape the Beta.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = category === c.key;
            return (
              <button
                key={c.key}
                data-testid={`feedback-cat-${c.key}`}
                onClick={() => setCategory(c.key)}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
              >
                <Icon className="w-3.5 h-3.5" /> {c.label}
              </button>
            );
          })}
        </div>

        <Textarea
          data-testid="feedback-message-input"
          autoFocus
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What's on your mind?"
          className="min-h-[120px] resize-none"
        />

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="feedback-cancel-btn" className="rounded-full">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!message.trim() || sending}
            data-testid="feedback-submit-btn"
            className="rounded-full bg-primary hover:bg-[#E85D04] text-white font-semibold gap-1.5"
          >
            <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
